import React, { useEffect, useState } from 'react';
import { apiClient } from '../api/client';

interface OrphanedGuest {
  id: string;
  fullName: string;
  chapterAffiliation: string | null;
  createdAt: string;
}

interface DuplicateGroup {
  name: string;
  records: Array<{
    id: string;
    registrationId: string | null;
    hasSeats: boolean;
  }>;
}

interface UnlinkedRegistration {
  id: string;
  userFullName: string;
  userEmail: string;
  eventName: string;
  createdAt: string;
}

interface SeatedWithoutReg {
  id: string;
  fullName: string;
  seatCompositeIds: string[];
}

interface LinkedWithoutSeat {
  id: string;
  fullName: string;
  registrationId: string;
  userFullName: string;
}

interface DuplicateSeat {
  id: string;
  fullName: string;
  seatCompositeIds: string[];
}

interface TicketWithoutReg {
  id: string;
  ticketNumber: number;
  guestName: string | null;
  tierName: string;
}

interface RegWithoutTicket {
  id: string;
  userFullName: string;
  userEmail: string;
  eventName: string;
}

interface DataIntegrityReportDto {
  orphanedGuests: { count: number; records: OrphanedGuest[] };
  duplicateGuests: { count: number; groups: DuplicateGroup[] };
  unlinkedRegistrations: { count: number; records: UnlinkedRegistration[] };
  seatedWithoutRegistration: { count: number; records: SeatedWithoutReg[] };
  linkedWithoutSeat: { count: number; records: LinkedWithoutSeat[] };
  duplicateSeatAssignments: { count: number; records: DuplicateSeat[] };
  ticketMismatches: {
    ticketsWithoutRegistration: { count: number; records: TicketWithoutReg[] };
    registrationsWithoutTicket: { count: number; records: RegWithoutTicket[] };
  };
}

export interface DataIntegrityModalProps {
  onClose: () => void;
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  content: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '28px',
    width: '680px',
    maxWidth: '90vw',
    maxHeight: '85vh',
    overflowY: 'auto' as const,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  title: {
    fontSize: '18px',
    fontWeight: 700,
    color: '#1a6b3c',
    margin: 0,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '22px',
    cursor: 'pointer',
    color: '#666',
    padding: '4px 8px',
    borderRadius: '4px',
  },
  loading: {
    textAlign: 'center' as const,
    padding: '3rem 0',
    color: '#666',
  },
  spinner: {
    display: 'inline-block',
    width: '32px',
    height: '32px',
    border: '3px solid #e0e0e0',
    borderTopColor: '#1a6b3c',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  error: {
    textAlign: 'center' as const,
    padding: '2rem 0',
  },
  errorText: {
    color: '#d32f2f',
    marginBottom: '12px',
    fontSize: '14px',
  },
  retryBtn: {
    padding: '8px 16px',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  grid: {
    display: 'grid',
    gap: '12px',
  },
  categoryCard: {
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  categoryHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px 16px',
    cursor: 'pointer',
    userSelect: 'none' as const,
    backgroundColor: '#fafafa',
  },
  categoryHeaderClean: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px 16px',
    backgroundColor: '#fafafa',
  },
  successIcon: {
    color: '#2e7d32',
    fontSize: '16px',
    fontWeight: 700,
  },
  warningIcon: {
    color: '#f57c00',
    fontSize: '16px',
    fontWeight: 700,
  },
  categoryName: {
    flex: 1,
    fontSize: '14px',
    fontWeight: 600,
    color: '#333',
  },
  countBadge: {
    fontSize: '12px',
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: '10px',
    backgroundColor: '#fff3e0',
    color: '#e65100',
  },
  countBadgeClean: {
    fontSize: '12px',
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: '10px',
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
  },
  expandArrow: {
    fontSize: '12px',
    color: '#999',
    transition: 'transform 0.2s',
  },
  detailList: {
    padding: '8px 16px 12px',
    borderTop: '1px solid #e0e0e0',
  },
  detailItem: {
    padding: '8px 0',
    borderBottom: '1px solid #f0f0f0',
    fontSize: '13px',
    color: '#444',
  },
  detailField: {
    display: 'inline-block',
    marginRight: '12px',
  },
  detailLabel: {
    color: '#888',
    fontSize: '11px',
    marginRight: '4px',
  },
};

const CATEGORY_LABELS: Record<string, string> = {
  orphanedGuests: 'Orphaned Guests',
  duplicateGuests: 'Duplicate Guests',
  unlinkedRegistrations: 'Unlinked Registrations',
  seatedWithoutRegistration: 'Seated Without Registration',
  linkedWithoutSeat: 'Linked Without Seat',
  duplicateSeatAssignments: 'Duplicate Seat Assignments',
  ticketMismatches: 'Ticket Mismatches',
};

export function DataIntegrityModal({ onClose }: DataIntegrityModalProps) {
  const [report, setReport] = useState<DataIntegrityReportDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiClient<DataIntegrityReportDto>('/reports/data-integrity');
      setReport(data);
    } catch (err: any) {
      setError(err?.message || 'Failed to fetch data integrity report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, []);

  const toggleExpand = (key: string) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const getCategoryCount = (key: string): number => {
    if (!report) return 0;
    if (key === 'ticketMismatches') {
      return (
        report.ticketMismatches.ticketsWithoutRegistration.count +
        report.ticketMismatches.registrationsWithoutTicket.count
      );
    }
    return (report as any)[key]?.count ?? 0;
  };

  const renderDetailList = (key: string) => {
    if (!report) return null;

    switch (key) {
      case 'orphanedGuests':
        return report.orphanedGuests.records.map((r) => (
          <div key={r.id} style={styles.detailItem}>
            <span style={styles.detailField}>
              <span style={styles.detailLabel}>Name:</span>
              {r.fullName}
            </span>
            <span style={styles.detailField}>
              <span style={styles.detailLabel}>Chapter:</span>
              {r.chapterAffiliation || '—'}
            </span>
            <span style={styles.detailField}>
              <span style={styles.detailLabel}>Created:</span>
              {new Date(r.createdAt).toLocaleDateString()}
            </span>
          </div>
        ));

      case 'duplicateGuests':
        return report.duplicateGuests.groups.map((g) => (
          <div key={g.name} style={{ ...styles.detailItem, borderBottom: '1px solid #e0e0e0' }}>
            <div style={{ fontWeight: 600, marginBottom: '4px' }}>"{g.name}" ({g.records.length} records)</div>
            {g.records.map((r) => (
              <div key={r.id} style={{ paddingLeft: '12px', fontSize: '12px', color: '#555', marginTop: '2px' }}>
                ID: {r.id.slice(0, 8)}… | Reg: {r.registrationId ? r.registrationId.slice(0, 8) + '…' : 'None'} | Seats: {r.hasSeats ? 'Yes' : 'No'}
              </div>
            ))}
          </div>
        ));

      case 'unlinkedRegistrations':
        return report.unlinkedRegistrations.records.map((r) => (
          <div key={r.id} style={styles.detailItem}>
            <span style={styles.detailField}>
              <span style={styles.detailLabel}>User:</span>
              {r.userFullName}
            </span>
            <span style={styles.detailField}>
              <span style={styles.detailLabel}>Email:</span>
              {r.userEmail}
            </span>
            <span style={styles.detailField}>
              <span style={styles.detailLabel}>Event:</span>
              {r.eventName}
            </span>
          </div>
        ));

      case 'seatedWithoutRegistration':
        return report.seatedWithoutRegistration.records.map((r) => (
          <div key={r.id} style={styles.detailItem}>
            <span style={styles.detailField}>
              <span style={styles.detailLabel}>Name:</span>
              {r.fullName}
            </span>
            <span style={styles.detailField}>
              <span style={styles.detailLabel}>Seats:</span>
              {r.seatCompositeIds.join(', ')}
            </span>
          </div>
        ));

      case 'linkedWithoutSeat':
        return report.linkedWithoutSeat.records.map((r) => (
          <div key={r.id} style={styles.detailItem}>
            <span style={styles.detailField}>
              <span style={styles.detailLabel}>Guest:</span>
              {r.fullName}
            </span>
            <span style={styles.detailField}>
              <span style={styles.detailLabel}>Registered by:</span>
              {r.userFullName}
            </span>
          </div>
        ));

      case 'duplicateSeatAssignments':
        return report.duplicateSeatAssignments.records.map((r) => (
          <div key={r.id} style={styles.detailItem}>
            <span style={styles.detailField}>
              <span style={styles.detailLabel}>Name:</span>
              {r.fullName}
            </span>
            <span style={styles.detailField}>
              <span style={styles.detailLabel}>Seats:</span>
              {r.seatCompositeIds.join(', ')}
            </span>
          </div>
        ));

      case 'ticketMismatches':
        return (
          <>
            {report.ticketMismatches.ticketsWithoutRegistration.count > 0 && (
              <div style={{ marginBottom: '8px' }}>
                <div style={{ fontWeight: 600, fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                  Tickets Without Registration ({report.ticketMismatches.ticketsWithoutRegistration.count})
                </div>
                {report.ticketMismatches.ticketsWithoutRegistration.records.map((r) => (
                  <div key={r.id} style={styles.detailItem}>
                    <span style={styles.detailField}>
                      <span style={styles.detailLabel}>#</span>
                      {r.ticketNumber}
                    </span>
                    <span style={styles.detailField}>
                      <span style={styles.detailLabel}>Guest:</span>
                      {r.guestName || '—'}
                    </span>
                    <span style={styles.detailField}>
                      <span style={styles.detailLabel}>Tier:</span>
                      {r.tierName}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {report.ticketMismatches.registrationsWithoutTicket.count > 0 && (
              <div>
                <div style={{ fontWeight: 600, fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                  Registrations Without Ticket ({report.ticketMismatches.registrationsWithoutTicket.count})
                </div>
                {report.ticketMismatches.registrationsWithoutTicket.records.map((r) => (
                  <div key={r.id} style={styles.detailItem}>
                    <span style={styles.detailField}>
                      <span style={styles.detailLabel}>User:</span>
                      {r.userFullName}
                    </span>
                    <span style={styles.detailField}>
                      <span style={styles.detailLabel}>Email:</span>
                      {r.userEmail}
                    </span>
                    <span style={styles.detailField}>
                      <span style={styles.detailLabel}>Event:</span>
                      {r.eventName}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div
      style={styles.overlay}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="data-integrity-modal-title"
    >
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div style={styles.content} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h3 id="data-integrity-modal-title" style={styles.title}>
            Data Integrity Audit
          </h3>
          <button
            style={styles.closeBtn}
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {loading && (
          <div style={styles.loading}>
            <div style={styles.spinner} />
            <p>Running audit checks…</p>
          </div>
        )}

        {error && !loading && (
          <div style={styles.error}>
            <p style={styles.errorText}>{error}</p>
            <button style={styles.retryBtn} onClick={fetchReport}>
              Retry
            </button>
          </div>
        )}

        {report && !loading && !error && (
          <div style={styles.grid}>
            {Object.keys(CATEGORY_LABELS).map((key) => {
              const count = getCategoryCount(key);
              const isClean = count === 0;
              const isExpanded = expanded[key];

              return (
                <div key={key} style={styles.categoryCard}>
                  {isClean ? (
                    <div style={styles.categoryHeaderClean}>
                      <span style={styles.successIcon}>✓</span>
                      <span style={styles.categoryName}>{CATEGORY_LABELS[key]}</span>
                      <span style={styles.countBadgeClean}>0</span>
                    </div>
                  ) : (
                    <>
                      <div
                        style={styles.categoryHeader}
                        onClick={() => toggleExpand(key)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter') toggleExpand(key); }}
                        aria-expanded={isExpanded}
                      >
                        <span style={styles.warningIcon}>⚠</span>
                        <span style={styles.categoryName}>{CATEGORY_LABELS[key]}</span>
                        <span style={styles.countBadge}>{count}</span>
                        <span style={{ ...styles.expandArrow, transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>
                          ▶
                        </span>
                      </div>
                      {isExpanded && (
                        <div style={styles.detailList}>
                          {renderDetailList(key)}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
